import { Link } from "react-router-dom";
import {
  ShieldCheck, Users, Plane, Car, Search, Award, ArrowRight,
  Star, Crosshair, Eye, Building2, ChevronRight
} from "lucide-react";
import LSSDLogo, { HERO_BG_URL } from "../components/LSSDLogo";

const TIMELINE = [
  { year: "1850", title: "Fondazione dello Stato", body: "Lo Stato di San Andreas viene ufficialmente costituito e con esso nasce l'esigenza di un corpo di pubblica sicurezza dedicato." },
  { year: "1852", title: "Nasce il LSSD", body: "Il Los Santos County Sheriff Department viene istituito come prima forza dell'ordine della Contea. Il motto \"A Tradition of Service\" viene scelto fin dal primo giorno." },
  { year: "1903", title: "Prima Stazione Centrale", body: "Inaugurata la storica Stazione di Vinewood, ancora oggi sede del Comando." },
  { year: "1947", title: "Motorizzazione", body: "Il LSSD diventa uno dei primi dipartimenti completamente motorizzati degli Stati Uniti." },
  { year: "1968", title: "Nascita del SEB", body: "Costituita la Special Enforcement Bureau per rispondere a situazioni ad alto rischio." },
  { year: "1972", title: "Aero Bureau", body: "Il primo elicottero decolla dalla Pillbox Heliport. Il LSSD opera dall'alto sulla Contea." },
  { year: "1985", title: "Detective Bureau", body: "Riorganizzato con squadre dedicate ad omicidi, narcotici, crimine organizzato e cold cases." },
  { year: "2010", title: "Era Digitale", body: "Introduzione del MDT, body cam, archivio digitale integrato in tutte le pattuglie." },
  { year: "Oggi", title: "Tradition of Service", body: "Oltre 50 operatori giurati, 7 divisioni operative, copertura H24 della Contea." },
];

const DIVISIONS = [
  { code: "SEB",    name: "Special Enforcement Bureau",    desc: "Unità speciale ad alto profilo. Gestisce situazioni di crisi, rilascio ostaggi, interventi armati ad alto rischio, irruzioni tattiche e protezione dignitari.", icon: Crosshair, tags: ["Tactical", "K9", "Hostage Rescue"] },
  { code: "TSD",    name: "Traffic Services Detail",       desc: "Unità stradale specializzata. Pattugliamento autostradale, incidenti gravi, indagini stradali, controlli DUI e sicurezza viaria.",                                icon: Car,       tags: ["Highway Patrol", "DUI Unit", "Crash Investigations"] },
  { code: "DB",     name: "Detective Bureau",              desc: "Investigatori in borghese. Squadre Omicidi, Narcotici, Crimine Organizzato, Frodi, Cold Cases.",                                                                    icon: Search,    tags: ["Homicide", "Narcotics", "Organized Crime"] },
  { code: "AERO",   name: "Aero Bureau",                   desc: "Operazioni aeree. Supporto agli inseguimenti, ricerca persone scomparse, sorveglianza dall'alto, evacuazioni mediche.",                                             icon: Plane,     tags: ["Maverick", "Air Support", "SAR"] },
  { code: "PATROL", name: "Patrol Division",               desc: "La spina dorsale del dipartimento. Pattuglie territoriali H24 in tutta la Contea: prima risposta, presenza visibile e capillare.",                                  icon: ShieldCheck, tags: ["First Responders", "24/7", "Community"] },
  { code: "CCD",    name: "Custody & Court Detail",        desc: "Sicurezza dei tribunali, gestione detenuti, trasferimenti penitenziari, custodia in attesa di giudizio.",                                                            icon: Building2, tags: ["Court Security", "Custody", "Transfers"] },
  { code: "IAB",    name: "Internal Affairs Bureau",       desc: "Affari Interni. Indaga sulla condotta degli operatori, garantisce l'integrità del Dipartimento.",                                                                    icon: Eye,       tags: ["Integrity", "Investigations", "Discipline"] },
  { code: "PAB",    name: "Personnel Administration",      desc: "Risorse Umane del Dipartimento. Gestisce reclutamenti, fascicoli personali, formazione e progressioni di carriera.",                                                icon: Users,     tags: ["Recruitment", "Training", "HR"] },
];

const STATS = [
  { value: "50+",  label: "Operatori Giurati",   icon: Users },
  { value: "7+",   label: "Divisioni Operative", icon: ShieldCheck },
  { value: "172",  label: "Anni di Servizio",    icon: Award },
  { value: "24/7", label: "Copertura",           icon: Star },
];

const CORE_VALUES = [
  { word: "Honor",     desc: "Onore in ogni decisione, rispetto verso colleghi e cittadini, lealtà alla divisa." },
  { word: "Integrity", desc: "Integrità sopra ogni cosa. La fiducia della comunità si conquista con la trasparenza." },
  { word: "Service",   desc: "Servizio alla comunità prima di tutto. La Contea è la nostra famiglia." },
];

export default function Storia() {
  return (
    <div data-testid="storia-page" className="bg-[var(--lssd-olive-deep)]">
      {/* HERO */}
      <section
        className="relative py-20 sm:py-28 lg:py-32 border-b border-[var(--lssd-khaki)]/20"
        style={{
          backgroundImage: `linear-gradient(rgba(28,36,21,0.85), rgba(28,36,21,0.95)), url(${HERO_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 relative z-10">
          <div className="max-w-3xl">
            <span className="chip-inst mb-6">La Nostra Storia</span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[var(--lssd-cream)] leading-[0.98] tracking-tight">
              Da oltre <span className="text-[var(--lssd-khaki)]">170 anni</span><br />
              al servizio della Contea.
            </h1>
            <p className="mt-6 text-lg text-[var(--lssd-cream)]/80 max-w-2xl leading-relaxed">
              Il Los Santos County Sheriff Department è la più antica e prestigiosa forza dell&apos;ordine
              dello Stato di San Andreas. Una tradizione di servizio che inizia nel 1852 e continua ancora oggi.
            </p>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-4" data-testid={`stat-${s.label}`}>
              <div className="h-12 w-12 rounded-full bg-[var(--lssd-khaki)]/12 border border-[var(--lssd-khaki)]/35 flex items-center justify-center shrink-0">
                <s.icon className="h-5 w-5 text-[var(--lssd-khaki)]" />
              </div>
              <div>
                <div className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)]/85 mt-1">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[var(--lssd-olive-deep)] py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="mb-14 text-center">
            <div className="kicker mb-3">Cronologia</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight">
              Le pietre miliari del <span className="text-[var(--lssd-khaki)]">Dipartimento</span>
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[var(--lssd-khaki)]/60 via-[var(--lssd-khaki)]/25 to-transparent" />
            <div className="space-y-8">
              {TIMELINE.map((t, i) => {
                const side = i % 2 === 0;
                return (
                  <div key={t.year} className={`relative flex flex-col sm:flex-row ${side ? "sm:flex-row" : "sm:flex-row-reverse"} items-start gap-6`} data-testid={`timeline-${t.year}`}>
                    <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-[var(--lssd-khaki)] border-4 border-[var(--lssd-olive-deep)] z-10 mt-2" />
                    <div className={`pl-12 sm:pl-0 sm:w-1/2 ${side ? "sm:pr-12 sm:text-right" : "sm:pl-12"}`}>
                      <div className="inline-block bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/20 rounded-2xl p-5 hover:border-[var(--lssd-khaki)]/60 transition-colors">
                        <div className="font-mono font-black text-[var(--lssd-khaki)] text-2xl mb-2">{t.year}</div>
                        <h3 className="font-display font-bold text-lg text-[var(--lssd-cream)] mb-2">{t.title}</h3>
                        <p className="text-sm text-[var(--lssd-cream)]/70 leading-relaxed text-left">{t.body}</p>
                      </div>
                    </div>
                    <div className="hidden sm:block sm:w-1/2" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="bg-[var(--lssd-olive-dark)] border-y border-[var(--lssd-khaki)]/15 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="kicker mb-3">Le Divisioni</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight leading-tight">
                Otto unità operative,<br /><span className="text-[var(--lssd-khaki)]">una sola missione.</span>
              </h2>
            </div>
            <p className="max-w-md text-[var(--lssd-cream)]/65 text-[15px] leading-relaxed">
              Il LSSD opera attraverso divisioni specializzate, ognuna con competenze specifiche
              per coprire ogni aspetto della sicurezza pubblica.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DIVISIONS.map((d) => (
              <Link
                to={`/divisioni/${d.code.toLowerCase()}`}
                key={d.code}
                data-testid={`division-${d.code}`}
                className="group relative bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/20 rounded-2xl p-6 hover:border-[var(--lssd-khaki)]/60 hover:-translate-y-0.5 transition-all"
              >
                <div className="h-11 w-11 rounded-full bg-[var(--lssd-khaki)]/12 border border-[var(--lssd-khaki)]/35 flex items-center justify-center mb-4 group-hover:bg-[var(--lssd-khaki)]/22 transition-colors">
                  <d.icon className="h-5 w-5 text-[var(--lssd-khaki)]" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-khaki)] mb-2">{d.code}</div>
                <h3 className="font-display font-bold text-lg text-[var(--lssd-cream)] mb-2 leading-tight">{d.name}</h3>
                <p className="text-sm text-[var(--lssd-cream)]/65 leading-relaxed mb-4">{d.desc}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {d.tags.map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-1 rounded-full bg-[var(--lssd-olive-deep)] border border-[var(--lssd-khaki)]/20 text-[var(--lssd-khaki)]">
                      {t}
                    </span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--lssd-khaki)]">
                  Scopri di più <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-[var(--lssd-olive-deep)] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-14">
            <div className="kicker mb-3">I Nostri Valori</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight">
              Honor · Integrity · <span className="text-[var(--lssd-khaki)]">Service</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {CORE_VALUES.map((v, i) => (
              <div key={v.word} className="bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/20 rounded-2xl p-8 text-center hover:border-[var(--lssd-khaki)]/60 transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--lssd-khaki)] mb-2">0{i + 1}</div>
                <h3 className="font-display font-black text-3xl text-[var(--lssd-cream)] mb-3 tracking-tight">{v.word}</h3>
                <p className="text-sm text-[var(--lssd-cream)]/70 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--lssd-olive-dark)] border-t border-[var(--lssd-khaki)]/20 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <LSSDLogo className="h-20 w-20 mx-auto mb-6" />
          <h2 className="font-display font-black text-3xl sm:text-5xl text-[var(--lssd-cream)] tracking-tight mb-4">
            Vuoi entrare a far parte<br />della <span className="text-[var(--lssd-khaki)]">tradizione?</span>
          </h2>
          <p className="text-[var(--lssd-cream)]/70 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Il Dipartimento sta cercando nuovi Vice Sceriffi. Se hai i requisiti, candidati ora.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/arruolamento" data-testid="storia-cta-recruit">
              <span className="btn-gold">Candidati Ora <ArrowRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/codice-penale" data-testid="storia-cta-penal">
              <span className="btn-inst-outline">Consulta il Codice Penale <ChevronRight className="h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
